import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api";

// Konfigurierbare Mail-Tiefe. Statt der bisher harten `top=25&days=14`-Query
// wählt der Nutzer, wie tief geladen wird — und zwar für ALLE drei Listen:
// Posteingang, Gesendet (`/mail/folder/{id}`) und Entwürfe (`/mail/drafts`).
// Die Werte bleiben innerhalb BEIDER Cap-Ebenen (maximal = genau die
// Obergrenze) → nie 422: Atlas-Router (app/routers/mail.py: `/inbox` +
// `/folder/{id}` top≤100 & days≤90, `/drafts` top≤100 OHNE days) UND
// email-service. Letzteres am LAUFENDEN System verifiziert (2026-07-26, rein
// lesend gegen http://n8n.bitop.de:8424/openapi.json — die von FastAPI aus
// eben jenen Query-Constraints erzeugte Vertragsbeschreibung):
//   /v1/mail/inbox/{upn}              top 1..100, days 1..90
//   /v1/mail/folder/{upn}/{folder_id} top 1..100, days 1..90
//   /v1/mail/drafts/{upn}             top 1..100, **kein days-Parameter**
// „maximal" (top=100) ist damit auch für Entwürfe gedeckt.
// Default "kompakt" = das bisherige Verhalten, also kein Bestands-Bruch.
//
// PERSISTENZ: server-seitig am NUTZER (`user_settings.payload.mail_inbox_depth`
// via GET/PUT /settings), NICHT im localStorage. Die Tiefe ist eine Einstellung
// des Nutzers, keine des Browsers → sie gilt geräteübergreifend und ist in den
// Einstellungen (Tab „E-Mail") als Standard festlegbar. Der frühere
// localStorage-Key `sb_mail_inbox_depth` ist ersatzlos entfallen; bestehende
// lokale Werte verfallen (bewusst — der Default bleibt "kompakt", es ändert
// sich also für niemanden etwas Unerwartetes).
export type InboxDepth = "kompakt" | "erweitert" | "maximal";

export const INBOX_DEPTH_PRESETS: Record<InboxDepth, { top: number; days: number }> = {
  kompakt: { top: 25, days: 14 },
  erweitert: { top: 50, days: 30 },
  maximal: { top: 100, days: 90 },
};

// Nur die WERTE (in Anzeige-Reihenfolge). Beschriftung + Hinweis kommen aus dem
// Katalog (`mail.seite.tiefe[wert]`, docs/i18n.md §3) — sonst stünde die Auswahl
// in der englischen Oberfläche deutsch unter ihrer übersetzten Kopfzeile.
export const INBOX_DEPTH_OPTIONS: { value: InboxDepth }[] = [
  { value: "kompakt" },
  { value: "erweitert" },
  { value: "maximal" },
];

// Reihenfolge für „Mehr laden" (Slice B): jeweils die nächsthöhere Stufe.
export const INBOX_DEPTH_ORDER: InboxDepth[] = ["kompakt", "erweitert", "maximal"];

/** Nächsthöhere Tiefe oder null, wenn bereits „maximal". */
export function nextInboxDepth(d: InboxDepth): InboxDepth | null {
  const i = INBOX_DEPTH_ORDER.indexOf(d);
  return i >= 0 && i < INBOX_DEPTH_ORDER.length - 1 ? INBOX_DEPTH_ORDER[i + 1] : null;
}

export const DEFAULT_INBOX_DEPTH: InboxDepth = "kompakt";

export function isInboxDepth(v: unknown): v is InboxDepth {
  return v === "kompakt" || v === "erweitert" || v === "maximal";
}

/** Nur das Feld, das uns interessiert — der Rest von SettingsOut ist egal. */
interface DepthSettingsResponse {
  mail_inbox_depth?: unknown;
}

export interface UseInboxDepth {
  depth: InboxDepth;
  /** false, solange die Nutzer-Einstellung noch geladen wird. Aufrufer, die
   *  daraus eine Query bauen, warten darauf — sonst feuert der erste Fetch mit
   *  dem Default und direkt danach ein zweiter mit dem echten Wert
   *  (Doppel-Fetch). */
  ready: boolean;
  saving: boolean;
  /** Optimistisch: setzt sofort + schreibt via PUT /settings. Bei Fehlschlag
   *  wird zurückgerollt. Liefert, ob gespeichert wurde. */
  setDepth: (d: InboxDepth) => Promise<boolean>;
}

export function useInboxDepth(): UseInboxDepth {
  const [depth, setDepthRaw] = useState<InboxDepth>(DEFAULT_INBOX_DEPTH);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    void api
      .get<DepthSettingsResponse>("/settings")
      .then((res) => {
        if (!aliveRef.current) return;
        // Defensiv: fehlendes/unbekanntes Feld (z.B. älteres Backend) darf die
        // Seite NICHT mitreißen — dann gilt schlicht der Default.
        const v = res?.mail_inbox_depth;
        if (isInboxDepth(v)) setDepthRaw(v);
      })
      .catch(() => {
        /* Settings nicht lesbar → Default. Kein Toast: die Mail-Liste soll
           laden, nicht wegen einer Präferenz blockieren. */
      })
      .finally(() => {
        if (aliveRef.current) setReady(true);
      });
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const setDepth = useCallback(
    async (d: InboxDepth): Promise<boolean> => {
      let prev: InboxDepth = DEFAULT_INBOX_DEPTH;
      setDepthRaw((cur) => {
        prev = cur;
        return d;
      });
      setSaving(true);
      try {
        // Partieller PUT: der Server merged nur dieses Feld (andere
        // Einstellungen bleiben unberührt).
        await api.put("/settings", { mail_inbox_depth: d });
        return true;
      } catch {
        if (aliveRef.current) setDepthRaw(prev);
        return false;
      } finally {
        if (aliveRef.current) setSaving(false);
      }
    },
    []
  );

  return { depth, ready, saving, setDepth };
}
